import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical } from "lucide-react";
import {
  useDeleteWithUndoController,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { defaultTaskCategories } from "../root/defaultConfiguration";
import type { Task as TData } from "../types";
import { MeetingSource } from "./MeetingSource";
import { TaskContactLink } from "./TaskContactLink";
import { PushToPlaneButton } from "./PushToPlaneButton";
import { CollaborationBadge } from "./CollaborationBadge";
import { DueDatePill } from "./DueDatePill";
import { AssigneePill } from "./AssigneePill";
import { TaskEdit } from "./TaskEdit";
import { TaskEditSheet } from "./TaskEditSheet";
import { useIsMobile } from "@/hooks/use-mobile";

export const Task = ({
  task,
  hideCheckbox,
  hideContact,
}: {
  task: TData;
  // Hide the built-in Done checkbox (and its mobile tap-to-complete) — used by
  // the Action Items list in select mode, where the leading checkbox is
  // repurposed for row selection instead.
  hideCheckbox?: boolean;
  // Hide the "about <contact>" line — used by the Contact page's own Action
  // Items section, where every row would link to the page already open.
  hideContact?: boolean;
}) => {
  const isMobile = useIsMobile();
  const { taskTypes, taskCategories } = useConfigurationContext();
  const notify = useNotify();
  const translate = useTranslate();
  const queryClient = useQueryClient();

  const [openEdit, setOpenEdit] = useState(false);
  const [startInEdit, setStartInEdit] = useState(false);

  const handleCloseEdit = () => setOpenEdit(false);

  const [update, { isPending: isUpdatePending, isSuccess, variables }] =
    useUpdate();
  const { handleDelete } = useDeleteWithUndoController({
    record: task,
    redirect: false,
    mutationOptions: {
      onSuccess() {
        notify("resources.tasks.deleted", { undoable: true });
      },
    },
  });

  const handleEdit = () => {
    setStartInEdit(true);
    setOpenEdit(true);
  };

  const handleView = () => {
    setStartInEdit(false);
    setOpenEdit(true);
  };

  const handleCheck = () => () => {
    update("tasks", {
      id: task.id,
      data: {
        done_date: task.done_date ? null : new Date().toISOString(),
      },
      previousData: task,
    });
  };

  const handleCopyLink = () => {
    void navigator.clipboard?.writeText(
      `${window.location.origin}/#/tasks?taskId=${task.id}`,
    );
    notify("Link copied", { type: "info" });
  };

  useEffect(() => {
    // We do not want to invalidate the query when a task is checked or unchecked
    if (
      isUpdatePending ||
      !isSuccess ||
      variables?.data?.done_date != undefined
    ) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["tasks", "getList"] });
  }, [queryClient, isUpdatePending, isSuccess, variables]);

  const labelId = `checkbox-list-label-${task.id}`;

  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <div
          className="flex items-start gap-2 flex-1 min-w-0"
          onClick={isMobile && !hideCheckbox ? handleCheck() : undefined}
        >
          {!hideCheckbox && (
            <Checkbox
              id={labelId}
              checked={!!task.done_date}
              onCheckedChange={handleCheck()}
              disabled={isUpdatePending}
              className="mt-1"
            />
          )}
          <div
            className={`min-w-0 flex-grow ${task.done_date ? "line-through" : ""}`}
          >
            <div
              className={`text-sm ${isMobile ? "" : "cursor-pointer"}`}
              onClick={isMobile ? undefined : handleView}
            >
              {task.type && task.type !== "none" && (
                <>
                  <span className="font-semibold text-sm">
                    {taskTypes.find((taskType) => taskType.value === task.type)
                      ?.label ?? task.type}
                  </span>
                  &nbsp;
                </>
              )}
              {task.category && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground mr-1">
                  {(taskCategories ?? defaultTaskCategories).find(
                    (c) => c.value === task.category,
                  )?.label ?? task.category}
                </span>
              )}
              {task.collaboration_id && (
                <CollaborationBadge
                  collaborationId={task.collaboration_id}
                  className="mr-1"
                />
              )}
              <PushToPlaneButton task={task} className="mr-1" />
              {task.text}
            </div>
            <div className="text-sm text-muted-foreground">
              {translate("resources.tasks.fields.due_short")}
              &nbsp;
              <DueDatePill task={task} />
            </div>
            <MeetingSource zoomMeetingId={task.zoom_meeting_id} />
            {!hideContact && <TaskContactLink contactId={task.contact_id} />}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <AssigneePill task={task} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 pr-0! size-8 cursor-pointer"
                aria-label={translate("resources.tasks.actions.title")}
              >
                <MoreVertical className="size-5 md:size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem
                className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
                onClick={handleCopyLink}
              >
                {translate("resources.tasks.actions.copy_link", {
                  _: "Copy link",
                })}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
                onClick={handleEdit}
              >
                {translate("ra.action.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
                onClick={handleDelete}
              >
                {translate("ra.action.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isMobile ? (
        <TaskEditSheet
          taskId={task.id}
          open={openEdit}
          onOpenChange={setOpenEdit}
        />
      ) : (
        <TaskEdit
          taskId={task.id}
          open={openEdit}
          close={handleCloseEdit}
          startInEdit={startInEdit}
        />
      )}
    </>
  );
};
